/**
 * CrmFeeModelsView — admin Fee Models tab. Lists the fee models (with proposal count + a
 * proportion-sanity badge); opens CrmFeeModelEditor. "Clone" duplicates a model's settings into a
 * new dated version so editing never disturbs a model that posted proposals reference.
 */
import { useEffect, useRef, useState } from 'react'
import { getFeeModels, cloneFeeModel } from '../../services/feeModels.js'
import CrmFeeModelEditor from './CrmFeeModelEditor.jsx'
import { promptDialog, alertDialog } from '../Dialog.jsx'

export default function CrmFeeModelsView() {
  const [models, setModels] = useState(null)
  const [openId, setOpenId] = useState(null)
  const [busy, setBusy] = useState(false)
  const loadedRef = useRef(false)

  function load() { setModels(null); getFeeModels().then(setModels).catch(() => setModels([])) }
  useEffect(() => { if (!loadedRef.current) { loadedRef.current = true; load() } }, [])

  async function clone(m) {
    const name = await promptDialog({ title: 'Clone fee model', message: 'Name for the new model:', defaultValue: `${m.name} (copy)`, required: true })
    if (!name) return
    setBusy(true)
    try { const created = await cloneFeeModel(m.id, name); load(); if (created) setOpenId(created.id) }
    catch (e) { await alertDialog({ title: 'Clone failed', message: (e && e.message) || 'Could not clone the fee model.' }) }
    finally { setBusy(false) }
  }

  return (
    <div style={S.wrap}>
      <div style={S.head}>
        <div>
          <div style={S.title}>Fee Models</div>
          <div style={S.sub}>The settings that drive proposal fees. Editing a model recomputes its linked proposals — clone a dated version rather than changing one that issued proposals reference.</div>
        </div>
      </div>

      <div style={S.list}>
        {models === null ? <div style={S.muted}>Loading…</div>
          : models.length === 0 ? <div style={S.muted}>No fee models.</div>
          : models.map(m => (
            <div key={m.id} style={S.row}>
              <button style={S.nameBtn} onClick={() => setOpenId(m.id)}>
                <span style={S.name}>{m.name}</span>
                <span style={S.meta}>{m.proposalCount} proposal{m.proposalCount === 1 ? '' : 's'}</span>
              </button>
              <span style={{ ...S.badge, color: m.basicOk ? '#1f9d57' : '#b3261e', background: (m.basicOk ? '#1f9d57' : '#b3261e') + '14' }}>
                {m.basicOk ? 'proportions ✓' : 'proportions ≠ 100%'}
              </span>
              <button style={S.cloneBtn} onClick={() => clone(m)} disabled={busy}>Clone</button>
            </div>
          ))}
      </div>

      {openId && <CrmFeeModelEditor modelId={openId} onClose={() => setOpenId(null)} onSaved={load} />}
    </div>
  )
}

const S = {
  wrap: { maxWidth: 820, margin: '0 auto', padding: '18px 20px 60px' },
  head: { marginBottom: 14 },
  title: { fontFamily: '"Montserrat", sans-serif', fontSize: 14, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--aj-text)' },
  sub: { fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: 'var(--aj-text-mid)', marginTop: 4, maxWidth: 620, lineHeight: 1.5 },
  list: { display: 'flex', flexDirection: 'column', background: 'var(--aj-bg-elevated)', borderRadius: 10, border: '0.5px solid var(--aj-border)', overflow: 'hidden' },
  row: { display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px', borderBottom: '0.5px solid var(--aj-border)' },
  nameBtn: { display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' },
  name: { fontFamily: '"DM Sans", sans-serif', fontSize: 13.5, fontWeight: 600, color: 'var(--aj-text)' },
  meta: { fontFamily: '"DM Mono", monospace', fontSize: 11, color: 'var(--aj-text-mid)', marginTop: 1 },
  badge: { fontFamily: '"DM Sans", sans-serif', fontSize: 11, fontWeight: 600, borderRadius: 20, padding: '3px 10px', flexShrink: 0 },
  cloneBtn: { fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: 'var(--aj-text)', background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 5, padding: '5px 12px', cursor: 'pointer', flexShrink: 0 },
  muted: { fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: 'var(--aj-text-mid)', fontStyle: 'italic', padding: '20px 14px', textAlign: 'center' },
}
